/**
 * Groups authorization write-backs by the calendar date of `created_at`.
 *
 * @module groupAuthorizationWritebacksByCreatedDate
 */
import { toUtcDateKey } from "../../../../../utils/formatDate";
import {
  CandidAuthorizationPayload,
  CandidWritebackRecord,
} from "../types/candidWriteback";
import { getAuthorizationPayloads } from "./getAuthorizationPayloads";

/** One authorization body plus the write-back row it came from. */
export interface AuthorizationPayloadWithRecord {
  /** Parent write-back row (status, `created_at`, ids). */
  record: CandidWritebackRecord;
  /** One entry from the row's authorization payload array. */
  payload: CandidAuthorizationPayload;
}

/**
 * Authorizations posted on a single UTC calendar day, for a sidenav tab.
 */
export interface AuthorizationWritebacksByDate {
  /** UTC calendar day (`YYYY-MM-DD`) from `created_at`. */
  dateKey: string;
  /** Display label for the sidenav tab (`MM/DD/YYYY`). */
  label: string;
  /** Flattened authorization bodies posted on this day. */
  items: AuthorizationPayloadWithRecord[];
}

/**
 * Formats a `YYYY-MM-DD` key as `MM/DD/YYYY` without timezone conversion.
 *
 * @param dateKey - UTC calendar day
 * @returns Sidenav tab label
 */
const toTabLabel = (dateKey: string): string => {
  const [year, month, day] = dateKey.split("-");
  if (!year || !month || !day) {
    return dateKey;
  }
  return `${month}/${day}/${year}`;
};

/**
 * Groups authorization write-backs by UTC `created_at` date and flattens
 * each row's payload array. Rows with no authorization bodies are skipped.
 * Groups are sorted oldest-first.
 *
 * @param records - Authorization write-back rows
 * @returns Date tabs with flattened payload items
 */
export const groupAuthorizationWritebacksByCreatedDate = (
  records: CandidWritebackRecord[],
): AuthorizationWritebacksByDate[] => {
  const byDate = new Map<string, AuthorizationPayloadWithRecord[]>();

  for (const record of records) {
    const payloads = getAuthorizationPayloads(record);
    if (payloads.length === 0) {
      continue;
    }
    const dateKey = toUtcDateKey(record.created_at);
    if (!dateKey) {
      continue;
    }
    const existing = byDate.get(dateKey) ?? [];
    for (const payload of payloads) {
      existing.push({ record, payload });
    }
    byDate.set(dateKey, existing);
  }

  return [...byDate.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([dateKey, items]) => ({
      dateKey,
      label: toTabLabel(dateKey),
      items,
    }));
};
